<!--
  File name: SecurityIncidentForm.vue
  Program Description:
    - This component renders the Annual Security Incident Report form.
    - When accessed, it displays all the necessary information needed from the
    report form including its status and history

-->

<!-- eslint-disable vue/no-lone-template -->
<template>
  <v-container>
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col>
        <v-card
          :key="operation"
          class="transparent-bg"
          tile
          elevation="0"
        >
          <v-card-title class="pl-0 pb-0">
            <h2 class="mr-3">
              <b>Annual Security Incident Report</b>
            </h2>
            <v-chip
              v-if="formItem.status === 'Submitted'"
              disabled
              class="ml-3"
              :color="`green lighten-5`"
            >
              <span
                class="green--text text--darken-1"
              >
                {{ formItem.status }}
              </span>
            </v-chip>
            <v-chip
              v-if="formItem.status === 'Drafted'"
              disabled
              class="ml-3"
              :color="`blue lighten-5`"
            >
              <span
                class="blue--text text--darken-1"
              >
                {{ formItem.status }}
              </span>
            </v-chip>
            <v-spacer />
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-stepper
          v-if="tabs"
          v-model="activeTab"
          editable
          flat
          class="pb-0 transparent-bg"
        >
          <v-stepper-header
            class="ma-0"
          >
            <template
              v-for="(header, index) in tabs"
            >
              <v-stepper-step
                :key="header.index"
                class="stepper step-number"
                :step="index+1"
                editable
              >
                <span
                  class="headerLabel"
                >
                  {{ header.name }}
                </span>
              </v-stepper-step>
              <v-divider
                v-if="index !== (tabs.length - 1 )&& !(index === 3 && (projectStatus === 'inDrafts' || projectStatus === ''))"
                :key="`divider-${index}`"
                class="mx-1"
              />
            </template>
          </v-stepper-header>
        </v-stepper>
      </v-col>
    </v-row>
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col class="mb-0 pb-0">
        <v-card
          :key="operation"
          tile
          elevation="0"
          outlined
        >
          <v-card-title class="pt-2 pb-2">
            <v-btn
              v-if="(!readOnly && !loading) && operation === 'create'"
              id="cancel-btn"
              color="grey darken-1"
              text
              @click="$router.push(backRoute)"
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="!readOnly && !loading && operation !== 'create'"
              id="cancel-edit-btn"
              color="red darken-1"
              text
              @click="operation !== 'view' ? confirmCancelEdit() : readOnly = true, view() "
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="!loading && operation === 'view'"
              id="back-btn"
              text
              @click="$router.push({name: 'SecurityIncidentTable'})"
            >
              <v-icon>mdi-arrow-left</v-icon> Back to ASIR Lists
            </v-btn>
            <v-spacer />
            <template>
              <v-btn
                v-for="(item) in formActions"
                v-show="!item.show || item.show(formItem)"
                :key="item.fn"
                v-bind="item.props"
                @click="run(item.fn)"
              >
                {{ item.label }}
              </v-btn>
              <v-btn
                v-if="showLabel(`canPost${roleName}`) && !readOnly && !viewCondition && !loading"
                id="save-draft-btn"
                color="primary"
                text
                type="button"
                :loading="loading"
                @click="confirmDialog()"
              >
                {{ getLabel(`canPost${roleName}`, 'Save as Draft') }}
              </v-btn>
              <v-btn
                v-if="showLabel(`canPost${roleName}`) && !readOnly && !viewCondition && !loading"
                id="submit-btn"
                color="primary"
                type="button"
                :disabled="!forSubmission"
                :loading="loading"
                @click="confirmDialog(true)"
              >
                {{ getLabel(`canPost${roleName}`, 'Submit Form' ) }}
              </v-btn>
              <v-btn
                v-if="isDraft && readOnly && showLabel(`canDelete${roleName}`) && !viewCondition && actionTabs && !loading"
                id="delete-btn"
                color="red darken-1"
                class="mr-2"
                text
                type="button"
                :disabled="deleteDisable"
                :loading="loading"
                @click.stop="checkDelete"
              >
                <v-icon>mdi-delete</v-icon>
                {{ getLabel(`canDelete${roleName}`, 'Delete') }}
              </v-btn>
              <v-btn
                v-if="isDraft && readOnly && showLabel(`canPatch${roleName}`) && !viewCondition && actionTabs && !loading"
                id="edit-btn"
                color="primary"
                :loading="loading"
                @click="operation !== 'update' ? $router.replace({ params: { operation: 'update', tab: activeTab } }) : readOnly = false"
              >
                <v-icon class="text-h6 mr-2">
                  {{ 'mdi-pencil' }}
                </v-icon>
                {{ getLabel(`canPatch${roleName}`, 'Edit Draft') }}
              </v-btn>
            </template>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col class="mt-0 pt-0">
        <v-card
          :key="operation"
          tile
          elevation="0"
          outlined
        >
          <v-card-text class="pt-0 ml-3">
            <v-container>
              <v-row
                v-show="operation === 'view' && !isLastTab"
                class="my-4"
              >
                <v-col>
                  <span class="d-block font-weight-bold">
                    ASIR Status
                  </span>
                  <v-chip
                    v-if="formItem.status === 'Submitted'"
                    disabled
                    :color="`green lighten-5`"
                  >
                    <span
                      class="green--text text--darken-1"
                    >
                      {{ formItem.status }}
                    </span>
                  </v-chip>
                  <v-chip
                    v-if="formItem.status === 'Drafted'"
                    disabled
                    :color="`blue lighten-5`"
                  >
                    <span
                      class="blue--text text--darken-1"
                    >
                      {{ formItem.status }}
                    </span>
                  </v-chip>
                </v-col>
                <v-spacer />
                <v-col>
                  <span class="d-block font-weight-bold">
                    Status History
                  </span>
                  <v-btn
                    color="#d6b315"
                    class="white--text"
                    @click="viewHistory"
                  >
                    <v-dialog
                      v-model="dialog"
                      max-width="800px"
                    >
                      <v-card
                        class="elevation-12 px-5 pt-3 pb-11 rounded-lg"
                        tile
                      >
                        <v-card-title
                          class="title"
                        >
                          <h1 class="primary--text text-h5 font-weight-bold">
                            Status History
                          </h1>
                        </v-card-title>
                        <v-card-text>
                          <v-row
                            class="mt-1"
                          >
                            <v-col cols="4">
                              <v-card-text class="font-weight-black text-body-1 py-0 px-5">
                                Status
                              </v-card-text>
                            </v-col>
                            <v-col
                              cols="4"
                              class="text-center"
                            >
                              <v-card-text class="font-weight-black text-body-1 py-0 px-5">
                                Date and Time
                              </v-card-text>
                            </v-col>
                            <v-col
                              cols="4"
                              class="text-center"
                            >
                              <v-card-text class="font-weight-black text-body-1 py-0 px-5">
                                Modified By
                              </v-card-text>
                            </v-col>
                          </v-row>
                          <v-row
                            v-for="object in statusHistory"
                            :key="object.id"
                          >
                            <v-col
                              cols="4"
                              class="py-1 px-5"
                            >
                              <span class="font-weight-black">
                                Asir has been {{ object.status }}
                              </span>
                            </v-col>
                            <v-col
                              cols="4"
                              class="py-1 px-5 text-center"
                            >
                              <span class="font-weight-black">
                                {{ object.date }}
                              </span>
                            </v-col>
                            <v-col
                              cols="4"
                              class="py-1 px-5 text-center"
                            >
                              <span class="font-weight-black">
                                {{ object.modifiedBy }}
                              </span>
                            </v-col>
                          </v-row>
                        </v-card-text>
                        <!-- <v-card-text>
                          <v-form lazy-validation>
                            <v-text-field
                              id="otp-field"
                              v-model.trim="pin"
                              :disabled="$data.$meta.isLoading"
                              label="One-time pin"
                              outlined
                              :error-messages="genericError('pin', 'OTP')"
                              @input="$v.pin.$touch()"
                              @blur="$v.pin.$touch()"
                            />
                          </v-form>
                        </v-card-text>
                        <v-card-subtitle class="py-0 black--text font-weight-bold">
                          <p class="mb-3">
                            Your One-Time Login Pin will expire in <u> {{ otpExpiry }} </u>
                          </p>
                        </v-card-subtitle>
                        <v-card-actions class="d-flex flex-column">
                          <v-btn
                            id="verify-otp-btn"
                            block
                            class="py-6"
                            color="primary"
                            :disabled="$data.$meta.isLoading"
                            @click="verify(false)"
                          >
                            Verify
                          </v-btn>
                          <v-spacer />
                          <v-btn
                            id="resend-otp-btn"
                            :loading="$data.$meta.isLoading"
                            class="py-6 primary--text font-weight-bold"
                            text
                            @click="resend(false)"
                          >
                            Resend OTP
                          </v-btn>
                        </v-card-actions> -->
                      </v-card>
                    </v-dialog>
                    <v-icon class="mr-1">
                      mdi-eye
                    </v-icon>
                    View
                  </v-btn>
                </v-col>
              </v-row>
              <v-tabs-items v-model="activeTabMinus">
                <v-tab-item
                  v-for="(tab, tabIndex) in currentForm"
                  :key="tabIndex"
                  :to="tab.route"
                >
                  <template v-if="tab.children">
                    <component
                      :is="field.is"
                      v-for="(field, fIndex) in tab.children"
                      v-show="field.hasOwnProperty('vShow') ? field['vShow'] : true"
                      :key="fIndex"
                      v-bind="field.vBind"
                    >
                      <template v-if="field.children">
                        <component
                          :is="child.is"
                          v-for="(child, cIndex) in field.children"
                          v-show="child.hasOwnProperty('vShow') ? child['vShow'] : true"
                          :key="cIndex"
                          v-bind="child.vBind"
                        >
                          <template v-if="child.children">
                            <template v-for="(gChild, gCIndex) in child.children">
                              <component
                                :is="gChild.is"
                                v-if="!gChild.header"
                                v-show="gChild.hasOwnProperty('vShow') ? gChild['vShow'] : true"
                                :key="gCIndex"
                                v-model="formItem[gChild.formKey]"
                                v-bind="gChild.vBind"
                                :error-messages="gChild.hasOwnProperty('formKey') && formValidations[gChild.formKey] ? genericError(`formItem.${gChild.formKey}`, gChild.vBind.label): [] "
                                v-on="gChild.hasOwnProperty('formKey') ? gChild.vOn : null"
                              >
                                <template
                                  v-if="gChild.info"
                                  slot="append"
                                >
                                  <v-tooltip
                                    max-width="400"
                                    right
                                  >
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon
                                        color="gray"
                                        depressed
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        mdi-information
                                      </v-icon>
                                    </template>
                                    <span>{{ gChild.info }}</span>
                                  </v-tooltip>
                                </template>
                              </component>
                              <component
                                :is="gChild.is"
                                v-else
                                :key="gCIndex"
                                v-bind="gChild.vBind"
                              >
                                {{ gChild.vText }}
                              </component>
                            </template>
                          </template>
                        </component>
                      </template>
                    </component>
                  </template>
                </v-tab-item>
              </v-tabs-items>
              <v-row class="pr-4 pt-12 pb-4">
                <v-btn
                  v-if="activeTab !== 1"
                  id="prev-btn"
                  text
                  type="button"
                  :loading="loading"
                  @click="prevTab"
                >
                  <v-icon class="mr-1">
                    {{ 'mdi-arrow-left' }}
                  </v-icon> Back
                </v-btn>
                <v-spacer />
                <v-btn
                  v-if="!forSubmission"
                  id="next-btn"
                  class="white--text"
                  color="#d6b315"
                  :loading="loading"
                  @click="nextTab"
                >
                  Continue <v-icon class="mr-1">
                    {{ 'mdi-arrow-right' }}
                  </v-icon>
                </v-btn>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-btn
      fab
      fixed
      bottom
      right
      class="primary btn--fab"
      @click="scrollToTop()"
    >
      <v-icon
        color="#FFFFFF"
      >
        mdi-arrow-up
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import crud from '../utils/rest/crud'
import validationErrors from '../utils/validations/errors'
import { validationMixin } from 'vuelidate'
import flattenFields from '@/meta/mdTable/utils/flattenFields'
import { privilegeMixin } from 'session-plugin'
import { capitalize, dataURLToFile, uploadNestedForm, convertObjId, dateAndTimeCreated } from '../utils/helpers'
import users from '@/utils/webapi/users'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityIncidentForm',
  mixins: [
    privilegeMixin,
    validationMixin
  ],
  props: {
    name: {
      type: String,
      default: ''
    },
    mdTables: {
      type: Object,
      default: undefined
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    parentId: {
      type: String,
      default: undefined
    }
  },
  data: () => ({
    loading: true,
    formItem: {},
    file: null,
    readOnly: true,
    activeTab: 1,
    forSubmission: false,
    isDraft: false,
    dialog: false,
    objToFlat: [
      'personalDataBreaches',
      'securityIncidentsOccured',
      'summary'
    ],
    statusHistory: [],
    disabledFields: []
  }),
  computed: {
    ...validationErrors,
    ...mapGetters('lookups', {
      profile: 'getCurrentUser'
    }),
    module () {
      return this.$props.mdTables[this.name](this)
    },
    form () {
      return this.module.form
    },
    createReroute () {
      return this.form.createReroute
    },
    backRoute () {
      return this.form.backRoute
    },
    tabs () {
      return this.form.tabs[this.dataTab].filter(x => !x.removeIn || (x.removeIn && !x.removeIn.includes(this.operation)))
    },
    fields () {
      return this.form.fields
    },
    schema () {
      return this.form.schema[this.dataTab]
    },
    nestedSchema () {
      return this.form.nestedSchema && this.form.nestedSchema[this.dataTab]
    },
    dataTab () {
      return this.$route.params.dataTab
    },
    currentForm () {
      return this.fields[this.dataTab]
    },
    currentTab () {
      return this.currentForm.find(x => x.tab === this.activeTab)
    },
    formActions () {
      return this.currentTab.formActions
    },
    formKeys () {
      return this.fields.asir[1].formKeys
    },
    optionID () {
      return this.currentTab.optionID
    },
    optionName () {
      return this.currentTab.optionName
    },
    optionQuery () {
      return this.currentTab.optionQuery
    },
    optionNested () {
      return this.currentTab.optionNested
    },
    optionView () {
      return this.currentTab.optionView
    },
    populateFetch () {
      return this.currentTab.populate
    },
    optionAction () {
      return this.currentTab.optionAction || 'lookups/getOptions'
    },
    id () {
      return this.$route.params.id
    },
    operation () {
      return this.$route.params.operation
    },
    collection () {
      return crud(this.dataTab)
    },
    roleName () {
      return capitalize(this.dataTab)
    },
    flatFields () {
      return flattenFields(this.currentTab.children)
    },
    currentFields () {
      return this.flatFields.filter(x => x.formKey)
    },
    fieldKeys () {
      return this.currentFields.map(x => x.formKey)
    },
    defaultItem () {
      const ret = {}
      this.currentFields
        .forEach(x => {
          ret[x.fieldName || x.value] = x.defaultValue
          this.$set(this.formItem, x.key || x.columnHeader.value, x.defaultValue || '')
        })
      return ret
    },
    collectionLabel () {
      return this.currentTab.label
    },
    collectionTitle () {
      return this.module.label
    },
    collectionHeader () {
      const header = `${this.capitalize(this.operation)} ${this.collectionLabel}`
      return this.module.allCapsHeader ? header.toUpperCase() : header
    },
    lookupState () {
      return this.$store.state.lookups[this.dataTab]
    },
    uploadQuery () {
      return this.form.uploadQuery
    },
    customItems () {
      return this.$store.state.lookups
    },
    watchFields () {
      return this.form.watchFields[this.dataTab]
    },
    objectId () {
      return this.form.convertToObjId[this.dataTab]
    },
    formValidations () {
      return this.form.validations[this.dataTab]
    },
    startIndex () {
      return (this.form.startIndex && this.form.startIndex[this.dataTab]) || 0
    },
    saveIndex () {
      return this.form.saveIndex[this.dataTab]
    },
    tabLength () {
      return this.saveIndex || this.tabs.length
    },
    tabGrow () {
      return this.tabs.length < 3
    },
    actionTabs () {
      return this.checkAction(this.activeTab)
    },
    isLastTab () {
      return this.$data.activeTab === this.tabLength
    },
    deleteDisable () {
      return this.module.deleteDisable
    },
    viewCondition () {
      return this.viewOnly || this.form.viewOnly
    },
    activeTabMinus () {
      return this.activeTab - 1
    },
    isPic () {
      return this.$s.userRole.includes('pic')
    },
    calcTotalIncidents () {
      const voluntary = this.formItem.voluntaryBreachNotification ? parseInt(this.formItem.voluntaryBreachNotification) : 0
      const mandatory = this.formItem.mandatoryBreachNotification ? parseInt(this.formItem.mandatoryBreachNotification) : 0
      const others = this.formItem.summaryOthers ? parseInt(this.formItem.summaryOthers) : 0
      return voluntary + mandatory + others
    },
    validTotal () {
      let total = 0
      for (const key of this.formKeys) {
        const val = parseInt(this.formItem[`${key}`])
        if (val) total += val
        else total += 0
      }
      return total === this.calcTotalIncidents
    },
    isDisabled () {
      return field => this.disabledFields.includes(field)
    }
  },
  watch: {
    activeTab: function () {
      this.$data.forSubmission = this.isLastTab
      if (!this.actionTabs) {
        this.readOnly = true
        this.view()
      }
    },
    readOnly: function () {
      this.$data.forSubmission = this.isLastTab
    },
    calcTotalIncidents: function () {
      this.formItem.total = this.calcTotalIncidents
    },
    formItem: {
      handler () {
        if (this.formItem.total === 0) return
        if (!this.validTotal) {
          this.disabledFields.forEach(key => this.$set(this.formItem, key, null))
          this.disabledFields = []
          return
        }
        for (const key of this.formKeys) {
          const val = parseInt(this.formItem[`${key}`])
          if (val) continue
          this.disabledFields.push(key)
          this.$set(this.formItem, key, 0)
        }
      },
      deep: true
    }
  },
  async created () {
    this.watchFields && this.watchFields.forEach((w) => {
      this.$watch(w.fieldName, w.function)
    })
    this.currentForm.forEach((currentTab) => {
      currentTab.watch && currentTab.watch.forEach((w) => {
        this.$watch(w.fieldName, w.function)
      })
    })

    !this.optionID && this.customOptionFetch()
    if (this.operation === 'create') {
      await this.getUserData()
      this.readOnly = false
    } else {
      await this.view()
      if (this.operation === 'update') this.readOnly = false
    }
    this.formItem.total = this.calcTotalIncidents
    this.loading = false
  },
  beforeMount () {
    this.$route.params.tab && (this.$data.activeTab = this.$route.params.tab)
  },
  methods: {
    confirmLeave () {
      return window.confirm('Are you sure you want to cancel? All unsaved data will be lost.')
    },
    confirmStayInDirtyForm () {
      if (!this.$refs.dataBreachForm.readOnly) {
        return !this.confirmLeave()
      }
    },
    confirmCancelEdit () {
      if (this.confirmLeave()) {
        this.$router.replace({ params: { operation: 'view', tab: this.activeTab } })
      }
    },
    capitalize,
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    toDateAndTime (date) {
      return dateAndTimeCreated(date)
    },
    checkAction (tab) {
      return (this.startIndex <= tab) && (!this.saveIndex || tab <= this.saveIndex)
    },
    validateRefs () {
      Object.keys(this.$refs).forEach((ref) => {
        this.$refs[`${ref}`].$v && this.$refs[`${ref}`].$v.$touch()
      })
    },
    async getUserData () {
      const resp = await users.profile()
      const currentUser = (await resp.json()).entry
      this.formItem.sectorName = currentUser.sector
      this.formItem.subSectorName = currentUser.subsector
      this.formItem.pic = currentUser.organization
    },
    confirmDialog (submit) {
      if (submit) {
        this.validateRefs()
        if (this.$v.$invalid) {
          this.$v.$touch()
          return this.$store.dispatch('showAlert', {
            message: 'Cannot save ASIR with incomplete information.',
            type: 'error'
          })
        }

        if (!this.validTotal) {
          return this.$store.dispatch('showAlert', {
            message: 'Total number of incidents on the summary page (page 1) must be equal to the total breakdown of incidents (page 2).',
            type: 'error'
          })
        }

        this.$store.dispatch('showDialog', {
          title: `Submit ${this.collectionLabel}`,
          titleColor: 'green',
          message: 'Are you sure you want to submit this form?',
          buttonLabel: 'Submit',
          buttonColor: 'green',
          callback: () => { this.save(true) }
        })
      } else {
        this.$store.dispatch('showDialog', {
          title: 'Save as Draft',
          message: 'You can save the changes and update it later.',
          buttonLabel: 'Save',
          buttonColor: 'primary',
          callback: () => { this.save() }
        })
      }
    },
    viewHistory () {
      this.dialog = true
    },
    customOptionFetch () {
      const populate = this.populateFetch ? 'populate=true' : ''
      if (!this.optionName.length) return
      if (typeof this.optionName === 'object') {
        this.optionName.forEach((name) => {
          const typeOfQuery = this.customItems.userList.includes(name) ? 'advancedQuery' : 'queries'
          const query = this.optionQuery[`${name}`] || ''
          const req = {}
          this.optionQuery && (req.additionalQuery = this.optionQuery[`${name}`])
          this.optionView && (req.view = this.optionView[`${name}`])
          this.$store.dispatch(this.optionAction, {
            type: name,
            query: `?${populate}&${typeOfQuery}=${encodeURI(JSON.stringify(query))}`,
            nested: this.optionNested,
            req
          })
        })
      } else {
        const query = this.optionQuery || ''
        const typeOfQuery = this.customItems.userList.includes(this.optionName) ? 'advancedQuery' : 'queries'
        this.$store.dispatch(this.optionAction, {
          type: this.optionName,
          query: `?${populate}&${typeOfQuery}=${encodeURI(JSON.stringify(query))}`,
          nested: this.optionNested,
          id: this.optionID,
          req: { additionalQuery: query, view: this.optionView }
        })
      }
    },
    async save (submit) {
      if (submit) {
        this.$v.$touch()
      }
      if (parseInt(this.formItem.others, 10) === 0) {
        this.$set(this.formItem, 'specify', '')
      }
      if (this.formItem.year < 2018) {
        return this.$store.dispatch('showAlert', {
          message: 'ASIR should be 2018 onwards.',
          type: 'error'
        })
      }
      this.readOnly = true
      if (this.operation === 'create') {
        await this.createItem(submit)
      } else {
        await this.updateItem(submit)
      }
      this.loading = false
    },
    async getImage (id, file, query) {
      if (!file) return ''
      const getFile = []
      for (let i = 0; i < file.length; i++) {
        const { filename, mimetype } = file[`${i}`]
        const data = await this.collection.getImage(id, filename, query)
        getFile.push(await dataURLToFile(data.file, filename, mimetype))
      }
      return getFile
    },
    async flattenObj (parentObj, obj) {
      const namesOfField = Object.keys(obj)
      await namesOfField.forEach((field) => {
        if (namesOfField.includes(field)) {
          if (parentObj === 'summary' && field === 'others') {
            this.$set(this.formItem, 'summaryOthers', obj[`${field}`])
          } else if (parentObj === 'securityIncidentsOccured' && field === 'others') {
            this.$set(this.formItem, 'others', obj[`${field}`])
          } else {
            this.$set(this.formItem, field, obj[`${field}`])
          }
        }
      })
    },
    async view () {
      let dataview = ''
      if (this.form.dataView) {
        dataview = this.form.dataView
      }
      const data = await this.collection.read(this.id, dataview)
      const keys = Object.keys(data.entry)
      if (data.entry.status === 'Drafted') {
        this.isDraft = true
      }
      if (data.entry.statusHistory) {
        this.statusHistory = data.entry.statusHistory.map(history => ({
          status: history.status,
          date: this.toDateAndTime(history.date),
          modifiedBy: history.modifiedBy
        }))
      }
      await keys.forEach((obj) => {
        if (this.objToFlat.includes(obj)) {
          data.entry[`${obj}`] && this.flattenObj(obj, data.entry[`${obj}`])
        } else if (obj === 'year') {
          this.$set(this.formItem, obj, data.entry[`${obj}`])
        } else {
          if (this.operation === 'update') {
            this.$set(this.formItem, 'sectorName', data.entry && data.entry.sector && data.entry.sector._id)
            this.$set(this.formItem, 'subSectorName', data.entry && data.entry.subSector && data.entry.subSector.length ? data.entry.subSector[0]._id : null)
          } else {
            this.$set(this.formItem, 'sectorName', data.entry.sectorName)
            this.$set(this.formItem, 'subSectorName', data.entry.subSectorName[0])
          }
          this.$set(this.formItem, 'pic', data.entry.pic)
          this.$set(this.formItem, 'city', data.entry.city)
          this.$set(this.formItem, 'status', data.entry.status)
          this.$set(this.formItem, 'dateCreated', data.entry.dateCreated)
        }
      })

      if (this.uploadQuery) {
        const image = await this.getImage(this.id, this.formItem[this.uploadQuery], this.uploadQuery)
        this.$set(this.formItem, this.uploadQuery, image)
      }
    },
    async createItem (submit) {
      this.loading = true
      let resp
      this.$set(this.formItem, 'status', submit ? 'Submitted' : 'Drafted')
      this.isPic && this.$set(this.formItem, 'createdBy', this.$s.user)
      this.objectId && convertObjId(this.objectId, this.schema)
      Object.keys(this.schema).forEach(key => !this.schema[`${key}`] && delete this.schema[`${key}`])
      const dataForm = await uploadNestedForm(this.schema, this.nestedSchema)
      if (this.uploadQuery) {
        resp = await this.collection.createForm(dataForm, this.uploadQuery)
      } else {
        resp = await this.collection.create(this.schema)
      }
      const data = await resp.json()
      this.triggerAlert({ success: resp.ok, action: 'create', data, submit })
    },
    async updateItem (submit) {
      this.loading = true
      let resp
      submit ? this.schema.status = 'Submitted' : this.schema.status = 'Drafted'
      this.objectId && convertObjId(this.objectId, this.schema)
      if (this.uploadQuery) {
        const dataForm = await uploadNestedForm(this.schema, this.nestedSchema)
        resp = await this.collection.updateForm(dataForm, this.uploadQuery, this.id)
      } else {
        resp = await this.collection.update(this.id, this.schema)
      }
      const data = await resp.json()
      this.triggerAlert({ success: resp.ok, action: 'update', data, submit })
    },
    async deleteItem () {
      this.loading = true
      const resp = await this.collection.delete(this.id)
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: 'ASIR Draft has been successfully deleted!',
          type: 'delete'
        })
        this.$router.push(this.backRoute)
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.loading = false
    },
    async run (fn, callback) {
      const { _id } = this.formItem
      await this.collection.run(fn)(_id)()
      this.formItem = await this.collection.read(_id)
    },
    upload () {
      return this.collection.upload({ file: this.file })
    },
    checkDelete () {
      this.$store.dispatch('showDialog', {
        title: `Delete ${this.collectionLabel}`,
        message: `Are you sure that you want to delete this ${this.collectionLabel}? This action cannot be undone.`,
        buttonLabel: 'Delete',
        callback: () => { this.deleteItem() }
      })
    },
    triggerAlert ({ success, action, data, submit }) {
      if (success) {
        if (!submit) {
          this.$store.dispatch('showAlert', {
            message: `Your ${this.collectionTitle} Form was successfully saved as draft!`,
            type: 'draft'
          })
        } else {
          this.$store.dispatch('showAlert', {
            message: `Your ${this.collectionTitle} Form has been successfully submitted!`,
            type: 'success'
          })
        }
        if (action !== 'view') {
          this.$router.push({
            name: this.createReroute,
            params: {
              dataTab: this.dataTab,
              operation: 'view',
              id: (data.entry || data.updatedResult)._id
            }
          })
        }
        this.readOnly = true
      } else {
        const error = (data.error || data.message).replace('Validation Error: ', '')
        this.$store.dispatch('showAlert', {
          message: error,
          type: 'error'
        })
        this.readOnly = false
      }
    },
    tabFields (tabId) {
      return this.flatFields.reduce((item, curr) => [...item, (curr.formKey && curr.formKey)], [])
    },
    validate () {
      this.tabFields(this.activeTab).forEach((f) => {
        if (this.$v.formItem[`${f}`]) this.$v.formItem[`${f}`].$touch()
      })
    },
    prevTab: function () {
      this.$data.activeTab = this.$data.activeTab === 1
        ? 1 : this.$data.activeTab - 1

      this.$data.forSubmission = false
    },
    nextTab: function () {
      const length = this.tabs.length
      this.activeTab = this.activeTab === length ? length
        : this.activeTab + 1

      this.$data.forSubmission = this.isLastTab
    },
    async autofillFunction (autofillURL) {
      const url = `${autofillURL}${this.formItem.student}`
      const res = await this.collection.autofill(url)
      const keys = Object.keys(res.entry)
      await keys.forEach((field) => {
        this.$set(this.formItem, field, res.entry[`${field}`])
      })
    }
  },
  validations () {
    return {
      formItem: this.formValidations
    }
  }
}
</script>

<style scoped>
  .v-stepper{
    box-shadow: none;
  }
  .transparent-bg {
    background-color: rgba(0,0,0,0);
  }
</style>
